import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { PlanView, ElevationView, planVB, elevVB } from './views';
import { Scene3D } from './ViewCanvas';
import { svgToJpeg } from './exportPdf';

// Offscreen full-frame renders used to build the PDF (SVG drawings + 3D snapshot)
export function ExportStage({ L, c, technical, onDone }) {
  const refs = { plan: useRef(null), elevation: useRef(null), elevationB: useRef(null), elevationC: useRef(null) };
  const vbs = { plan: planVB(L), elevation: elevVB(L, 'A'), elevationB: elevVB(L, 'B'), elevationC: elevVB(L, 'C') };
  const shot = useRef(null);
  const shotP = useRef(null);
  if (!shotP.current) shotP.current = new Promise((r) => { shot.current = r; });
  const onCapture = useCallback((data) => shot.current(data), []);
  useEffect(() => {
    let alive = true;
    const run = async () => {
      await new Promise((r) => setTimeout(r, 60));
      const out = {};
      for (const k of Object.keys(refs)) if (refs[k].current) out[k] = await svgToJpeg(refs[k].current, vbs[k]);
      const data = await Promise.race([shotP.current, new Promise((r) => setTimeout(() => r(null), 60000))]);
      if (data) out.perspective = { data, ratio: 1000 / 1600 };
      if (alive) onDone(out);
    };
    run().catch((e) => alive && onDone(null, e));
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const noop = () => {};
  const st = { position: 'fixed', left: -20000, top: 0, width: 1600, pointerEvents: 'none' };
  return (
    <div style={st} aria-hidden="true">
      <PlanView L={L} c={{ ...c, p: 'xp', F: (id) => `url(#xp-${id})` }} p="xp" sel={null} onPick={noop} technical={technical} vb={vbs.plan} svgRef={refs.plan} />
      <ElevationView L={L} c={{ ...c, p: 'xe', F: (id) => `url(#xe-${id})` }} p="xe" sel={null} onPick={noop} vb={vbs.elevation} svgRef={refs.elevation} wall="A" />
      {L.walls.includes('B') && <ElevationView L={L} c={{ ...c, p: 'xb', F: (id) => `url(#xb-${id})` }} p="xb" sel={null} onPick={noop} vb={vbs.elevationB} svgRef={refs.elevationB} wall="B" />}
      {L.walls.includes('C') && <ElevationView L={L} c={{ ...c, p: 'xc', F: (id) => `url(#xc-${id})` }} p="xc" sel={null} onPick={noop} vb={vbs.elevationC} svgRef={refs.elevationC} wall="C" />}
      <div style={{ width: 1600, height: 1000 }}>
        <Suspense fallback={null}><Scene3D L={L} c={c} sel={null} controls={false} onCapture={onCapture} testid="export-scene3d" /></Suspense>
      </div>
    </div>
  );
}
